import { useQueryClient } from '@tanstack/react-query';
import { components, paths } from 'src/generated/v1';
import API_BASE_URL from 'src/constants';
import logger from 'src/utils/logger';
import { $api } from './api';
import { handleUnauthorized } from './authToken';
import { EntityCandidate } from './chat';

// FIXME(be-merge): The backend feature branch adds `description`, `category` and
// `event_type` to WorkflowTrigger. Those fields aren't yet in the merged OpenAPI
// schema, so we extend the generated type locally to keep consumers compiling.
// Once the BE branch is merged and `yarn generate:api-code` re-runs, delete the
// intersection below — `components['schemas']['WorkflowTrigger']` will already
// include these fields.
export type Trigger = components['schemas']['WorkflowTrigger'] & {
  description?: string | null;
  category?: string | null;
  event_type?: string | null;
};
export type TriggerGroup = Omit<components['schemas']['WorkflowTriggerGroup'], 'items'> & {
  items: Trigger[];
};
export type Workflow = components['schemas']['WorkflowList'];
export type WorkflowRun = components['schemas']['WorkflowRun'];
export type WorkflowRunStats = components['schemas']['WorkflowRunStats'];
export type WorkflowRunStatus = NonNullable<WorkflowRun['status']>;
export type WorkflowRunStep = components['schemas']['WorkflowRunStep'];
export type WorkflowDryRun = components['schemas']['WorkflowDryRun'];
export type WorkflowBlueprint = components['schemas']['WorkflowBlueprint'];
export type WorkflowItemModel = components['schemas']['WorkflowItemModel'];
export type WorkflowConditionPreview = components['schemas']['WorkflowConditionPreview'];
export type WorkflowCreateInput = components['schemas']['WorkflowCreate-Input'];

export const useWorkflowsTriggersQuery = () => {
  // FIXME(be-merge): cast to extended TriggerGroup[] to surface the optional
  // `description` / `category` / `event_type` fields the BE branch will provide.
  // Remove the cast once the BE branch is merged.
  const query = $api.useQuery('get', '/workflows/triggers/');
  return query as typeof query & { data: TriggerGroup[] | undefined };
};

export const useWorkflowsQuery = (params?: paths['/workflows']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/workflows', {
    params: {
      query: params,
    },
  });
};

export const useActivateWorkflow = () => {
  const queryClient = useQueryClient();

  return $api.useMutation('put', '/workflows/workflow/{id}/activate/{is_active}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get', '/workflows'],
      });
    },
  });
};

export const useArchiveWorkflow = () => {
  const queryClient = useQueryClient();

  return $api.useMutation('delete', '/workflows/workflow/{id}', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get', '/workflows'],
      });
    },
  });
};

export const useWorkflowsActionsQuery = () => {
  return $api.useQuery('get', '/workflows/actions/');
};

export const useWorkflowsActionsPlaceholdersQuery = (model: string, enabled: boolean) => {
  return $api.useQuery(
    'get',
    '/workflows/actions/mail_placeholder/{model}',
    {
      params: {
        path: {
          model,
        },
      },
    },
    {
      enabled: enabled && !!model,
    },
  );
};

export const useWorkflowsTemplatesQuery = (model: string, enabled: boolean, scope?: 'mail' | 'case') => {
  return $api.useQuery(
    'get',
    '/workflows/templates/{model}',
    {
      params: {
        path: {
          model,
        },
        query: {
          scope,
        },
      },
    },
    {
      enabled: enabled && !!model,
    },
  );
};

export const useWorkflowsConditionsAttributesQuery = (model: string, enabled: boolean) => {
  return $api.useQuery(
    'get',
    '/workflows/conditions/attributes/{model}',
    {
      params: {
        path: {
          model,
        },
      },
    },
    {
      enabled: enabled && !!model,
    },
  );
};

export const useWorkflowsConditionsQuery = () => {
  return $api.useQuery('get', '/workflows/conditions/');
};

export const useWorkflowsConditionsValuesQuery = (attribute: string, enabled: boolean) => {
  return $api.useQuery(
    'get',
    '/workflows/conditions/values/{attribute}',
    {
      params: {
        path: {
          attribute,
        },
      },
    },
    {
      enabled: enabled && !!attribute,
    },
  );
};

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/workflows/workflow', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/workflows'] });
    },
  });
};

export const useUpdateWorkflow = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/workflows/workflow/{id}', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/workflows'] });
      queryClient.removeQueries({ queryKey: ['get', '/workflows/workflow/{id}'] });
    },
  });
};

export const useWorkflowRunsQuery = (
  workflowId: number,
  params: { page?: number; size?: number; run_status?: WorkflowRunStatus },
  enabled = true,
) => {
  return $api.useQuery(
    'get',
    '/workflows/workflow/{workflow_id}/runs',
    {
      params: {
        path: { workflow_id: workflowId },
        query: params,
      },
    },
    { enabled: enabled && Number.isFinite(workflowId) },
  );
};

export const useGetWorkflow = (id: number) => {
  return $api.useQuery('get', '/workflows/workflow/{id}', {
    params: {
      query: { workflow_id: id },
    },
  });
};

export const useWorkflowBlueprintsQuery = (enabled = true) => {
  return $api.useQuery('get', '/workflows/blueprints', {}, { enabled });
};

export const useDryRunWorkflow = () => {
  return $api.useMutation('post', '/workflows/workflow/{workflow_id}/dry-run');
};

export const useConditionPreview = () => {
  return $api.useMutation('post', '/workflows/conditions/preview');
};

export const searchWorkflowRecords = async (query: string, model: string): Promise<EntityCandidate[]> => {
  const accessToken = localStorage.getItem('access_token');
  try {
    const response = await fetch(
      `${API_BASE_URL}portal/utility/chatter/linkable?q=${encodeURIComponent(query)}&type=${encodeURIComponent(model)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!response.ok) {
      if (response.status === 401) handleUnauthorized(accessToken);
      return [];
    }
    return (await response.json()) as EntityCandidate[];
  } catch (err) {
    logger.error('Failed to search workflow records', err);
    return [];
  }
};
